import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { FundCalendarStateAtom } from "@/store/FundCalendarStateAtom";
import { FundAtom, SelectedFundAtom } from "@/store/FundStateAtom";
import { ReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { getAccounts } from "@/api/Accounts";
import { getInstruments } from "@/api/Instruments";
import { getFundCalendars } from "@/api/FundCalendar";
import { getHoldings } from "@/api/Holdings";
import { getTransactions } from "@/api/Transactions";
import { getReportingPeriods } from "@/api/ReportingPeriods";
import { GettingStartedData } from "./gettingStartedSteps";

export const useGettingStartedData = () => {
  const fundCalendars = useAtomValue(FundCalendarStateAtom);
  const funds = useAtomValue(FundAtom);
  const selectedFund = useAtomValue(SelectedFundAtom);
  const reportingPeriods = useAtomValue(ReportingPeriodAtom);
  
  const [accounts, setAccounts] = useState<any[]>([]);
  const [instruments, setInstruments] = useState<any[]>([]);
  const [actualFundCalendars, setActualFundCalendars] = useState<any[]>([]);
  const [holdings, setHoldings] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [actualReportingPeriods, setActualReportingPeriods] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch all data that doesn't require fund ID
        const [accountsData, instrumentsData, fundCalendarsData] = await Promise.all([
          getAccounts(),
          getInstruments(),
          getFundCalendars()
        ]);
        
        setAccounts(accountsData || []);
        setInstruments(instrumentsData || []);
        setActualFundCalendars(fundCalendarsData || []);
        
        // Fetch data that requires fund ID if a fund is selected
        if (selectedFund && selectedFund.fundId) {
          const fundId = selectedFund.fundId;
          const [holdingsData, transactionsData, reportingPeriodsData] = await Promise.all([
            getHoldings(fundId),
            getTransactions(fundId),
            getReportingPeriods(fundId.toString())
          ]);
          
          setHoldings(holdingsData || []);
          setTransactions(transactionsData || []);
          setActualReportingPeriods(reportingPeriodsData || []);
        }
      } catch (error) {
        console.error("Error fetching getting started data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [selectedFund]);

  // Calculate completion states
  const hasFundCalendars = actualFundCalendars.length > 0;
  const hasFunds = funds.length > 0;
  const hasFundSelected = !!selectedFund;
  const hasAccounts = accounts.length > 0;
  const hasInstruments = instruments.length > 0;
  
  // Check if there's at least one transaction or holding that has been confirmed
  const hasValidatedNavPack = transactions.some((t: any) => t.isConfirmed === true) || 
                             holdings.some((h: any) => h.isConfirmed === true);
  
  // Check if there's at least one reporting period that has been closed
  const hasClosedPeriod = actualReportingPeriods.some((p: any) => p.isOpen === false);
  
  // Mock state for steps that don't have API endpoints yet
  const hasResolvedExceptions = false;
  
  // Split the transactions and holdings check
  const hasTransactions = transactions && transactions.length > 0;
  const hasHoldings = holdings && holdings.length > 0;

  const data: GettingStartedData = {
    hasFundCalendars,
    hasFunds,
    hasAccounts,
    hasInstruments,
    hasFundSelected,
    hasTransactions,
    hasHoldings,
    hasValidatedNavPack,
    hasClosedPeriod,
    hasResolvedExceptions,
  };

  return {
    data,
    isLoading,
    // Raw data if needed
    accounts,
    instruments,
    actualFundCalendars,
    holdings,
    transactions,
    actualReportingPeriods,
  };
}; 